// frontend/src/components/AppearancePanel.tsx
// Self-contained, embeddable appearance panel.
// Drop it anywhere with one line: <AppearancePanel />
// No floating button, no slide animation — renders inline inside any page.

import React from 'react';
import { motion } from 'framer-motion';
import Divider from '@mui/material/Divider';
import CheckIcon from '@mui/icons-material/Check';
import PaletteOutlinedIcon from '@mui/icons-material/PaletteOutlined';

import { useShieldTheme } from '../theme/ThemeContext';
import {
  ALL_THEMES,
  DesignTokens,
  ThemeId,
} from '../theme/tokens';

// ── Props ─────────────────────────────────────────────────────────────────────

interface AppearancePanelProps {
  /** Show the "Appearance" header. Default: true */
  showTitle?: boolean;
  /** Compact mode hides descriptions and uses smaller swatches. Default: false */
  compact?: boolean;
  /** Admin mode adds the production-lock instructions. Default: false */
  adminMode?: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AppearancePanel({
  showTitle = true,
  compact   = false,
  adminMode = false,
}: AppearancePanelProps) {
  const { themeId, tokens: t, setTheme } = useShieldTheme();

  return (
    <div style={{
      background:   t.bgSurface,
      border:       `1px solid ${t.borderDefault}`,
      borderRadius: t.radiusLg,
      overflow:     'hidden',
      boxShadow:    t.shadowCard,
    }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      {showTitle && (
        <div style={{
          padding:      compact ? '14px 18px 12px' : '18px 22px 14px',
          borderBottom: `1px solid ${t.borderSubtle}`,
          display:      'flex',
          alignItems:   'center',
          gap:          '10px',
        }}>
          <PaletteOutlinedIcon style={{ color: t.primary, fontSize: 20 }} />
          <div>
            <div style={{
              fontFamily: t.fontDisplay,
              fontSize:   compact ? '14px' : '16px',
              fontWeight: 700,
              color:      t.textPrimary,
            }}>
              Appearance
            </div>
            {!compact && (
              <div style={{ fontSize: '12px', color: t.textMuted, marginTop: '1px' }}>
                Customize your Shield experience. Saved automatically.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Body ────────────────────────────────────────────────────────── */}
      <div style={{ padding: compact ? '14px 16px' : '18px 20px' }}>

        {/* ── Theme cards ───────────────────────────────────────────────── */}
        <div style={{
          display:             'grid',
          gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))',
          gap:                 '10px',
        }}>
          {Object.values(ALL_THEMES).map(th => {
            const active = th.id === themeId;
            return (
              <ThemeCard
                key={th.id}
                th={th}
                active={active}
                compact={compact}
                t={t}
                onSelect={() => setTheme(th.id as ThemeId)}
              />
            );
          })}
        </div>

        {/* Font preview */}
        <div style={{
          marginTop:    '16px',
          padding:      '12px 16px',
          background:   t.bgSubtle,
          borderRadius: t.radiusMd,
          border:       `1px solid ${t.borderSubtle}`,
        }}>
          <div style={{
            fontSize:      '11px',
            color:         t.textMuted,
            fontWeight:    600,
            marginBottom:  '6px',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}>
            Font Preview
          </div>
          <div style={{
            fontFamily: ALL_THEMES[themeId].fontDisplay,
            fontSize:   '18px',
            color:      t.textPrimary,
            fontWeight: 700,
          }}>
            Shield Security Platform
          </div>
          <div style={{
            fontFamily: ALL_THEMES[themeId].fontBody,
            fontSize:   '13px',
            color:      t.textSecondary,
            marginTop:  '2px',
          }}>
            27-dimension behavioral fingerprint active
          </div>
          <div style={{
            fontFamily: ALL_THEMES[themeId].fontMono,
            fontSize:   '11px',
            color:      t.accent,
            marginTop:  '4px',
          }}>
            SNAP-ENGINE :: risk_score=87 :: threat=HIGH
          </div>
        </div>

        {/* ── Admin note ────────────────────────────────────────────────── */}
        {adminMode && (
          <>
            <Divider style={{ margin: '18px 0', borderColor: t.borderSubtle }} />
            <div style={{
              padding:      '12px 14px',
              background:   t.warningMuted,
              borderRadius: t.radiusMd,
              border:       `1px solid ${t.warning}33`,
              fontSize:     '12px',
              color:        t.textSecondary,
              lineHeight:   1.6,
            }}>
              <span style={{ fontWeight: 700, color: t.warning }}>Admin note: </span>
              To lock all users to a specific theme for production,
              edit{' '}
              <code style={{ fontFamily: t.fontMono, color: t.accent, fontSize: '11px' }}>
                src/theme/adminThemeConfig.ts
              </code>{' '}
              and uncomment{' '}
              <code style={{ fontFamily: t.fontMono, color: t.accent, fontSize: '11px' }}>forceTheme</code>.
              To add a new theme, add one <code style={{ fontFamily: t.fontMono, color: t.accent, fontSize: '11px' }}>DesignTokens</code> object
              to{' '}
              <code style={{ fontFamily: t.fontMono, color: t.accent, fontSize: '11px' }}>tokens.ts</code>
              {' '}and register it in <code style={{ fontFamily: t.fontMono, color: t.accent, fontSize: '11px' }}>ALL_THEMES</code>.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── ThemeCard (extracted to keep the parent lean) ─────────────────────────────

interface ThemeCardProps {
  th: DesignTokens;
  active: boolean;
  compact: boolean;
  t: DesignTokens;
  onSelect: () => void;
}

function ThemeCard({ th, active, compact, t, onSelect }: ThemeCardProps) {
  return (
    <motion.button
      onClick={onSelect}
      whileHover={{ scale: 1.015 }}
      whileTap={{ scale: 0.98 }}
      style={{
        background:   active ? t.primaryMuted : t.bgSubtle,
        border:       `2px solid ${active ? t.primary : t.borderSubtle}`,
        borderRadius: t.radiusMd,
        cursor:       'pointer',
        padding:      compact ? '10px 12px' : '14px 16px',
        display:      'flex',
        alignItems:   'center',
        gap:          '12px',
        transition:   'all 0.18s',
        textAlign:    'left',
        boxShadow:    active ? t.shadowGlow : 'none',
      }}
    >
      {/* Color swatches */}
      <div style={{ display: 'flex', gap: '3px', flexShrink: 0 }}>
        {([th.bgCanvas, th.primary, th.accent] as string[]).map((color, i) => (
          <div key={i} style={{
            width:        compact ? 20 : 26,
            height:       compact ? 20 : 26,
            borderRadius: t.radiusSm,
            background:   color,
            border:       `1px solid ${th.borderDefault}`,
          }} />
        ))}
      </div>

      {/* Labels */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: t.fontBody,
          fontWeight: 700,
          fontSize:   compact ? '13px' : '14px',
          color:      t.textPrimary,
          display:    'flex',
          alignItems: 'center',
          gap:        '6px',
        }}>
          {th.name}
          <span style={{
            fontSize:     '10px',
            padding:      '1px 6px',
            borderRadius: t.radiusFull,
            background:   th.mode === 'dark' ? t.bgElevated : t.borderSubtle,
            color:        t.textMuted,
            fontWeight:   500,
          }}>
            {th.mode}
          </span>
        </div>
        {!compact && (
          <div style={{ fontSize: '12px', color: t.textMuted, marginTop: '2px' }}>
            {th.description}
          </div>
        )}
      </div>

      {/* Checkmark */}
      {active && (
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          style={{
            width:          20,
            height:         20,
            borderRadius:   t.radiusFull,
            background:     t.primary,
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            flexShrink:     0,
          }}
        >
          <CheckIcon style={{ fontSize: 12, color: t.textOnPrimary }} />
        </motion.div>
      )}
    </motion.button>
  );
}
